// When to re-read a food the app copied into the user's own database, and how to tell whether the
// server's version would actually change anything.
//
// The problem this exists for: the app copies a food into the user's database the first time they
// use it, and before this it NEVER refreshed that copy (`FoodDbService.getFoodByIdAsync` returns
// the copy without reading the shared database at all, and the Add Foods list renders straight off
// it). So a correction made on the server — a fixed nutrient, or the pictures the official recipes
// were given in 2026-09 — never reached anybody who had already used that food. Worse for recipes
// than for anything else: every paying user gets a copy of ALL the official recipes on first sign
// in, so for them nothing on the server ever showed up.
//
// The rule the owner asked for (2026-09-20): when a food is first OPENED, or first SEEN as a card
// in search, check whether the server has a newer version and replace the copy if so — once per
// food per app start, and only when the server can actually be reached.
//
// Kept apart from `FoodDbService` so these decisions can be unit-tested without a database, a
// network or Angular. The service owns the "checked already this boot" record and the reads; this
// module owns the judgements.

import { isEqual } from 'lodash';
import { Food, FoodStatus } from '@cf-shared/data-model/food';
import { isStapleId } from 'services/staple-food-read';

/**
 * Whether this food is worth asking the server about at all.
 *
 * Only OFFICIAL foods — the `food/$` ids: the curated recipes, the official ingredients, and the
 * imported sources such as USDA, Open Food Facts, NFSA and OpenNutrition. Those are the foods
 * nobody but us edits, so the server's version is always the better one and replacing a copy with
 * it can only be an improvement.
 *
 * What is deliberately left out, and why each one:
 *
 *  - anything that is not a food document, since only `food/` ids live in the shared food database.
 *  - a STAPLE. Staples already have a fresher path of their own: the device keeps a downloaded
 *    snapshot of the curated staples, and `getFoodByIdAsync` answers a `food/$STP-` id from that,
 *    ahead of both the user's copy and the server (`design-docs/food-staples.md`, "Reading a staple
 *    food"). Asking the server for one would be a slower way to get an older answer.
 *  - a PRIVATE food, which was never published, so there is no server copy to compare against.
 *  - a MEMBER food (`food/+UD…`), which a PERSON authors and edits. This one is a safety decision
 *    rather than a scoping one, and it was made during review: publishing a member food goes
 *    through a cloud function that can fail, which leaves the author's edited copy in their own
 *    database while the server still holds the older document. Refreshing from the server would
 *    then quietly throw their edit away. Leaving member foods out keeps exactly the behaviour they
 *    have today, which loses nothing that used to work.
 */
export function isFoodWorthCheckingForFreshness(food: { _id?: unknown; status?: FoodStatus } | null | undefined): boolean {
  // guard the shape: this runs on documents read from a database, which may be damaged or old
  if (!food || typeof food._id !== 'string' || !food._id.startsWith('food/$')) {
    return false;
  }
  if (isStapleId(food._id)) {
    return false;
  }
  return food.status !== FoodStatus.Private;
}

/**
 * Whether replacing the user's copy with the server's version would change anything they can see.
 *
 * This is deliberately a comparison of CONTENT rather than of version numbers. A copy records
 * nothing about which server version it came from — `doCacheFoodAsync` drops `_rev` when it writes
 * the copy — so a version comparison would need a new field on every food and a migration for the
 * copies that already exist. Comparing content needs neither, and it answers the question that
 * actually matters: would the user see something different?
 *
 * It mirrors exactly what the write does. `doCacheFoodAsync` takes every key of the incoming food
 * except `_id` and `_rev` and assigns it onto the existing copy; it never deletes a key. So the
 * copy changes if and only if one of those assignments would land a different value. Comparing the
 * same set of keys means this function can never answer "changed" for a write that would in fact
 * be a no-op, which would make every read write, and push a revision to the server and to the
 * user's other devices, for nothing.
 *
 * `local` is excluded because it is the one part of a food that is about the USER — their like
 * mark, when they last used it, the measure they last chose. A server document must not carry one
 * (`shared/src/data-model/food.ts`), and the write preserves theirs; excluding it here keeps the
 * two halves saying the same thing.
 */
export function serverCopyWouldChangeCachedCopy(cachedCopy: Food, serverCopy: Food): boolean {
  // guard the shape: either side may be missing or damaged, and "cannot tell" must not be read
  // as "needs replacing" — that would rewrite the copy on every single read
  if (!cachedCopy || !serverCopy || typeof cachedCopy !== 'object' || typeof serverCopy !== 'object') {
    return false;
  }
  for (const key of Object.keys(serverCopy)) {
    if (key === '_id' || key === '_rev' || key === 'local') {
      continue;
    }
    if (!isEqual((cachedCopy as unknown as Record<string, unknown>)[key], (serverCopy as unknown as Record<string, unknown>)[key])) {
      return true;
    }
  }
  return false;
}

/**
 * Remembers which foods have already been asked about since the app started.
 *
 * Why once per app start, and not once ever: a correction can land on the server at any time, and
 * nothing tells the app when. Why not every time the food is read: `getFoodByIdAsync` runs for
 * every ingredient of every meal opened, so an unbounded check would put a network read in front
 * of each of them. Once per food per start is the owner's rule (2026-09-20), and it makes the cost
 * of the whole feature a function of how many distinct foods a person looks at in one sitting.
 *
 * It lives only in memory, so closing the app forgets everything — which is the point: the next
 * start checks again.
 */
export class FoodFreshnessLog {
  private readonly checked = new Set<string>();
  /** asked about right now, by a read that has not come back yet; see `claimIdsToCheck` */
  private readonly inFlight = new Set<string>();
  /** while the clock is before this, every check is skipped; see `pauseAfterFailure` */
  private pausedUntilMs = 0;

  /**
   * Take the foods out of `ids` that should be asked about now, and claim them.
   *
   * Claiming is what makes "once per app start" true when 2 things ask at the same time. The Add
   * Foods list checks the rows it is showing while a food the person just opened is checked on its
   * own, and both used to pass this filter before either had finished, so the same food was read
   * twice — and the second write could be dropped by the de-duplication inside `cacheFoodAsync`
   * while the food was already recorded as done, leaving the correction unseen until the next app
   * start. A claim must be released by `markChecked` or `releaseClaim`, whichever the caller
   * reaches.
   */
  claimIdsToCheck(ids: readonly string[], nowMs: number): string[] {
    if (nowMs < this.pausedUntilMs) {
      return [];
    }
    const claimed = ids.filter(id => !this.checked.has(id) && !this.inFlight.has(id));
    claimed.forEach(id => this.inFlight.add(id));
    return claimed;
  }

  /** Remember that the server has answered for these, so they are not asked about again. */
  markChecked(ids: readonly string[]): void {
    ids.forEach(id => {
      this.checked.add(id);
      this.inFlight.delete(id);
    });
  }

  /**
   * Give up a claim without recording an answer, so these foods are asked about again.
   *
   * Used when the read did not come back with anything to judge — the shared database could not be
   * reached, or the wait for it ran past its deadline.
   */
  releaseClaim(ids: readonly string[]): void {
    ids.forEach(id => this.inFlight.delete(id));
  }

  /**
   * Stop checking for a while, after a read that could not reach the server.
   *
   * The device being offline is the ordinary reason, and it is not an error: the user keeps seeing
   * the copy they have, exactly as before. What this prevents is a failed read for every food on
   * every screen for as long as the connection is down. Nothing is marked as checked, so once the
   * pause is over the same foods are asked about again.
   */
  pauseAfterFailure(nowMs: number, pauseMs: number): void {
    this.pausedUntilMs = nowMs + pauseMs;
  }

  /** How many foods have been asked about since the app started. Read by the tests. */
  get checkedCount(): number {
    return this.checked.size;
  }
}

/** How long to stop checking after a read that could not reach the server. */
export const FOOD_FRESHNESS_PAUSE_AFTER_FAILURE_MS = 60_000;

/**
 * How long one freshness read may take before it is given up on.
 *
 * It needs a deadline rather than just a `catch`, because the read does not simply fail when there
 * is no connection: it first waits for the shared food database to be attached, and that wait can
 * take a long time or never finish at all. Without a deadline, the read that answers "open this
 * food" would never return on an offline cold start — and that read runs for every ingredient of
 * every meal opened. The Add Foods page reaches the same conclusion for the same read and uses the
 * same 3 seconds (`PICKED_STAPLE_READ_LIMIT_MS` in `client/src/pages/foods/foods.ts`).
 */
export const FOOD_FRESHNESS_READ_LIMIT_MS = 3000;

/** What `readWhenReadyWithinDeadlineAsync` answers when the deadline came first. */
export const FRESHNESS_READ_GAVE_UP = Symbol('the freshness read ran past its deadline');

/**
 * Wait for `whenReady`, then run `read`, all within `limitMs`. Answers the read's result, or
 * `FRESHNESS_READ_GAVE_UP` when the deadline came first.
 *
 * The wait for `whenReady` is the reason this exists (df-fd5e5dd18008). The food service is
 * started after navigation can already have opened a meal, and before then it has no database
 * to read from at all, so a read that does not wait first fails with a TypeError that looks like
 * a real problem. Waiting inside the deadline turns "asked too early" into the same quiet
 * give-up as "no connection".
 *
 * When the deadline wins, the read is not started late: nobody would use its answer. A failure
 * of the read itself is passed on to the caller unchanged.
 */
export async function readWhenReadyWithinDeadlineAsync<T>(
  whenReady: PromiseLike<unknown>,
  read: () => Promise<T>,
  limitMs: number,
): Promise<T | typeof FRESHNESS_READ_GAVE_UP> {
  let gaveUp = false;
  let timer: ReturnType<typeof setTimeout> | undefined = undefined;
  try {
    return await Promise.race([
      (async () => {
        await whenReady;
        // if the deadline already passed, nobody is waiting for this read any more
        if (gaveUp) return FRESHNESS_READ_GAVE_UP;
        return read();
      })(),
      new Promise<typeof FRESHNESS_READ_GAVE_UP>(resolve => {
        timer = setTimeout(() => {
          gaveUp = true;
          resolve(FRESHNESS_READ_GAVE_UP);
        }, limitMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}
